import { trpc } from '@/utils/trpc';

// Query keys for lecturer subjects (kept for cache invalidation if needed)
export const lecturerSubjectQueryKeys = {
  all: ['lecturer-subjects'] as const,
  mySubjects: (tenantId: string) =>
    [...lecturerSubjectQueryKeys.all, 'my-subjects', tenantId] as const,
  subject: (tenantId: string, id: string) =>
    [...lecturerSubjectQueryKeys.all, 'subject', tenantId, id] as const,
};

/**
 * Hook to get subjects where current user has access based on their roles
 * Uses tRPC listForUser for role-based subject filtering:
 * - LECTURER/COURSE_REP with subject-specific role → only that subject
 * - LECTURER/COURSE_REP with domain role → all subjects in domain
 * - Admin roles → all subjects in context
 *
 * @param userId - UUID of the current user
 * @param tenantId - UUID of the tenant context
 * @param domainId - Optional domain context filter
 * @param enabled - Whether to enable the query (default: true)
 */
export const useMySubjects = (
  userId: string,
  tenantId: string,
  domainId?: string,
  enabled: boolean = true
) => {
  return trpc.subject.listForUser.useQuery(
    { userId, tenantId, domainId },
    {
      enabled: enabled && !!userId && !!tenantId,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

/**
 * Hook to get specific subject info by ID
 * Uses tRPC getById for type-safe API calls
 *
 * @param tenantId - UUID of the parent tenant
 * @param subjectId - UUID of the subject
 */
export const useSubjectInfo = (tenantId: string, subjectId: string) => {
  return trpc.subject.getById.useQuery(
    { tenantId, subjectId },
    {
      enabled: !!tenantId && !!subjectId,
      staleTime: 10 * 60 * 1000, // 10 minutes - subject info doesn't change often
    }
  );
};
